/**
 * Analytics API Service
 * 
 * Handles GA4 analytics data.
 * Migrated from seo-engine-webtools-customer-module
 */

import customerApi, { USE_MOCK_DATA } from './customerApi';
import { AnalyticsSummaryResponse } from '@/types/customer';

// Mock data for development
const analyticsMockData: AnalyticsSummaryResponse = {
  siteId: 1,
  dateRange: { startDate: '2026-01-01', endDate: '2026-01-22' },
  summary: {
    totalSessions: 125000,
    totalUsers: 89000,
    newUsers: 45000,
    engagementRate: 0.68,
    avgSessionDuration: 185,
    bounceRate: 0.32,
    screenPageViews: 312000,
  },
  topPages: [
    { pagePath: '/home', pageTitle: 'Home', pageViews: 45000, uniquePageViews: 38000, avgTimeOnPage: 120 },
    { pagePath: '/features', pageTitle: 'Features', pageViews: 32000, uniquePageViews: 28000, avgTimeOnPage: 180 },
    { pagePath: '/pricing', pageTitle: 'Pricing', pageViews: 28000, uniquePageViews: 24000, avgTimeOnPage: 95 },
    { pagePath: '/blog', pageTitle: 'Blog', pageViews: 22000, uniquePageViews: 18000, avgTimeOnPage: 240 },
    { pagePath: '/contact', pageTitle: 'Contact', pageViews: 15000, uniquePageViews: 13000, avgTimeOnPage: 60 },
  ],
  topSources: [
    { source: 'google', medium: 'organic', sessions: 52000, users: 38000, bounceRate: 0.28 },
    { source: 'direct', medium: '(none)', sessions: 28000, users: 22000, bounceRate: 0.35 },
    { source: 'facebook', medium: 'social', sessions: 15000, users: 12000, bounceRate: 0.42 },
    { source: 'linkedin', medium: 'social', sessions: 12000, users: 9500, bounceRate: 0.38 },
    { source: 'twitter', medium: 'social', sessions: 8000, users: 6500, bounceRate: 0.45 },
  ],
  dailyTrend: [
    { date: '2026-01-15', sessions: 5200, users: 3800, pageViews: 13500 },
    { date: '2026-01-16', sessions: 5400, users: 3950, pageViews: 14200 },
    { date: '2026-01-17', sessions: 5100, users: 3700, pageViews: 13100 },
    { date: '2026-01-18', sessions: 4800, users: 3500, pageViews: 12400 },
    { date: '2026-01-19', sessions: 5600, users: 4100, pageViews: 14800 },
    { date: '2026-01-20', sessions: 5900, users: 4300, pageViews: 15500 },
    { date: '2026-01-21', sessions: 6100, users: 4500, pageViews: 16200 },
  ],
};

export interface GetAnalyticsParams {
  startDate: string;
  endDate: string;
  groupBy?: 'page_path' | 'source_medium' | 'device_category';
}

/**
 * Get analytics data for a site
 */
export async function getAnalyticsData(
  siteId: number,
  params: GetAnalyticsParams
): Promise<AnalyticsSummaryResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return analyticsMockData;
  }

  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.groupBy) {
    queryParams.set('groupBy', params.groupBy);
  }

  const response = await customerApi.get<{ success: boolean; data: AnalyticsSummaryResponse }>(
    `/webtools/sites/${siteId}/analytics/daily?${queryParams}`
  );
  return response.data.data;
}

export default {
  getAnalyticsData,
};
